import { Ref, useEffect, useImperativeHandle, useState } from 'react';
import { DeepPartial, FieldValues, UseFormReturn } from 'react-hook-form';

import { WizardRef } from '../../WizardState';
import { useWizardState } from '../useWizardState/useWizardState';

interface UseWizardStateHandlerProps<TFormState extends FieldValues, TWizardState> {
  form: UseFormReturn<TFormState>;
  toWizardState: (formData: TFormState) => DeepPartial<TWizardState>;
  createInitialFormState?: (state: TWizardState) => TFormState;
  ref: Ref<any>;
}

export const useWizardFormHandle = <TFormState extends FieldValues, TWizardState>({
  form,
  toWizardState,
  createInitialFormState,
  ref,
}: UseWizardStateHandlerProps<TFormState, TWizardState>) => {
  const [initialValues, setInitialValues] = useState<TFormState>();
  const { state, updateState } = useWizardState<TWizardState>();

  useImperativeHandle<WizardRef, any>(ref, () => {
    return {
      onSubmit: async () => {
        const success = await form.trigger();
        if (success) {
          const newState = { ...state, ...toWizardState(form.getValues()) };
          updateState(newState);
        }
        return success;
      },
    };
  }, [form, state, toWizardState, updateState]);

  useEffect(() => {
    if (createInitialFormState) {
      const initialDefaultValues = createInitialFormState?.(state);
      setInitialValues(initialDefaultValues);
      form.reset(initialDefaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { initialValues };
};
